import type { CrossmintFrameServices } from "../index";

type Awaitable<T> = T | PromiseLike<T>;
export async function measureFunctionTime<T>(
	fn: () => T | Promise<T>,
): Promise<{ result: T; time: number }> {
	const start = performance.now();
	const result = await fn();
	const end = performance.now();
	return { result, time: Math.round(end - start) };
}

/**
 * Retries an async callback with configurable backoff delays.
 * Calls onError for each failed attempt. Throws the last error if all retries are exhausted.
 */
export async function withRetries<T>(
	fn: () => Promise<T>,
	options: {
		retryDelays: number[];
		onError?: (error: unknown, attempt: number, maxAttempts: number) => void;
		onRetry?: (attempt: number, delayMs: number) => void;
	},
): Promise<T> {
	const maxAttempts = options.retryDelays.length + 1;
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			return await fn();
		} catch (error: unknown) {
			options.onError?.(error, attempt, maxAttempts);
			if (attempt < maxAttempts) {
				const delay = options.retryDelays[attempt - 1];
				options.onRetry?.(attempt, delay);
				await new Promise((resolve) => setTimeout(resolve, delay));
			} else {
				throw error;
			}
		}
	}
	// Unreachable, but needed for TypeScript
	throw new Error("withRetries: exhausted all attempts");
}

/**
 * Collects debug information from all services that implement safeDebugState
 * @param services The services object containing all frame services
 * @returns Promise resolving to debug information from all services
 */
export async function collectSafeDebugState(
	services: CrossmintFrameServices,
): Promise<Record<string, unknown>> {
	const debugInfo: Record<string, unknown> = {};

	for (const [serviceName, service] of Object.entries(services)) {
		if (service.safeDebugState) {
			try {
				debugInfo[serviceName] = await service.safeDebugState();
			} catch {
				debugInfo[serviceName] = {
					error: "Failed to collect debug info",
				};
			}
		}
	}

	return debugInfo;
}
